import {EventEmitterMixin} from "../mixins";
import type {CompositionEvents, CompositionSettings, CompositionState} from "./composition.types";
import {Serializer} from "../services";
import {autoDetectRenderer, Container, Graphics, Renderer} from "pixi.js";
import type {float, frame, hex} from '../types';
import {BaseError} from "../errors";
import {CompositionEventsEnum} from "../../librettoEditor/CompositionEvents";
import {EngineInternalEventsEnum} from "../EngineInternalEvents";
import {FPS_DEFAULT, Timestamp} from "../models";
import {Track} from "../tracks";
import {AudioClip, Clip, MediaClip} from "../clips";
import {TrackDeserializer, TrackType} from "../tracks";
import {AudioMixer, MixerTrackHandle} from "../media/audio-mixer";
import {VideoClip} from "../clips/video/video";
import {ScriptLayerClip} from "../clips/script/script";

const DEBUG_ENGINE_LOGS =
    typeof window !== "undefined" && (window as any).__LIBRETTO_DEBUG_ENGINE_LOGS === true;

type ClockMode = 'audio' | 'timer';

type SceneTransitionRuntimeInfo =
    | {
          fromSceneIndex: number;
          toSceneIndex: number;
          fromSceneKey: string;
          toSceneKey: string;
          progress: number;
          transitionType: string;
          easing?: string;
          addFades: boolean;
      }
    | null;

const clamp01 = (t: number): number => Math.max(0, Math.min(1, t));

const normalizeHexColor = (c: unknown): string | null => {
    if (typeof c === "string") {
        const s = c.trim();
        if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
        if (/^0x[0-9a-fA-F]{6}$/.test(s)) return `#${s.slice(2).toLowerCase()}`;
    }
    if (typeof c === "number" && Number.isFinite(c)) {
        const n = Math.max(0, Math.min(0xffffff, Math.floor(c)));
        return `#${n.toString(16).padStart(6, "0")}`;
    }
    return null;
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const s = normalizeHexColor(hex);
    if (!s) return null;
    const n = Number.parseInt(s.slice(1), 16);
    if (!Number.isFinite(n)) return null;
    return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
};

const rgbToHex = (rgb: { r: number; g: number; b: number }): string => {
    const r = Math.max(0, Math.min(255, Math.round(rgb.r)));
    const g = Math.max(0, Math.min(255, Math.round(rgb.g)));
    const b = Math.max(0, Math.min(255, Math.round(rgb.b)));
    const n = (r << 16) | (g << 8) | b;
    return `#${n.toString(16).padStart(6, "0")}`;
};

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

const lerpColorHex = (fromHex: string, toHex: string, t: number): string => {
    const a = hexToRgb(fromHex) ?? { r: 0, g: 0, b: 0 };
    const b = hexToRgb(toHex) ?? { r: 0, g: 0, b: 0 };
    return rgbToHex({
        r: lerp(a.r, b.r, t),
        g: lerp(a.g, b.g, t),
        b: lerp(a.b, b.b, t),
    });
};

const toPixiClearColor = (hex: string): number => {
    const s = normalizeHexColor(hex) ?? "#000000";
    const n = Number.parseInt(s.slice(1), 16);
    return Number.isFinite(n) ? n : 0x000000;
};

const easeInOutCubic = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOutBack = (t: number): number => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};
const easeOutElastic = (t: number): number => {
    if (t === 0 || t === 1) return t;
    const c4 = (2 * Math.PI) / 3;
    return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
};
const easeOutBounce = (t: number): number => {
    const n1 = 7.5625;
    const d1 = 2.75;
    if (t < 1 / d1) return n1 * t * t;
    if (t < 2 / d1) {
        const tt = t - 1.5 / d1;
        return n1 * tt * tt + 0.75;
    }
    if (t < 2.5 / d1) {
        const tt = t - 2.25 / d1;
        return n1 * tt * tt + 0.9375;
    }
    const tt = t - 2.625 / d1;
    return n1 * tt * tt + 0.984375;
};
const easeOutExpo = (t: number): number => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

const applyEasing = (t: number, easing?: string): number => {
    const key = String(easing ?? "natural").trim().toLowerCase();
    const clamped = clamp01(t);
    switch (key) {
        case "none":
            return clamped;
        case "speed_up":
            return clamped * clamped;
        case "slow_down":
            return 1 - Math.pow(1 - clamped, 2);
        case "swing":
        case "natural":
            return easeInOutCubic(clamped);
        case "overshoot":
            return easeOutBack(clamped);
        case "elastic_out":
            return easeOutElastic(clamped);
        case "bounce_out":
            return easeOutBounce(clamped);
        case "impulse":
            return easeOutExpo(clamped);
        default:
            return easeInOutCubic(clamped);
    }
};

export class Composition extends EventEmitterMixin<CompositionEvents, typeof Serializer>(Serializer) {

    public compositionId: string;
    public isEmpty: boolean;

    private _disposed: boolean = false;

    private _duration = new Timestamp();
    private audioCtx!: AudioContext;
    private masterGain!: GainNode;
    public mixer?: AudioMixer;

    public renderer?: Renderer;

    public stage = new Container();

    public settings: CompositionSettings;

    public tracks: Track<Clip>[] = [];

    // The current frame playback is set to.
    public frame: frame = 0;

    public playbackRate: float = 1;

    // Canvas element to render to on the dom.
    public canvas?: HTMLCanvasElement;

    // Context for the canvas element.
    public context?: CanvasRenderingContext2D;

    public state: CompositionState = 'IDLE';

    public fps: float = FPS_DEFAULT;

    // clock selection
    private clockMode: ClockMode = 'timer';
    private compStartAtPlay = 0;          // comp seconds at play()
    private audioCtxStartAtPlay = 0;      // seconds
    private wallNowStartMs = 0;           // performance.now() at play()

    // render ticker
    private rafId?: number;
    private bgIntervalId?: number;

    // Script layer: special, single track abstraction for transcript-driven editing
    public scriptLayer: { layerId: string | null; clipId: string } | null = null;

    private _renderOnInitListenerId: string | null = null;

    /**
     * Current script-layer scene key at the composition playhead.
     * Keyed by ScriptLayerScene.startSplitId with "__start__" for the first scene.
     * Updated once per computeFrame() so visual clips can apply per-scene overrides cheaply.
     */
    public currentSceneKey: string = "__start__";

    /**
     * Current script-layer scene transition info (when within a transition window).
     * Used by non-script clips to interpolate per-scene overrides.
     */
    public currentSceneTransition: SceneTransitionRuntimeInfo = null;

    /**
     * Fullscreen fade-to-black overlay used by the scene "Fade" transition.
     * Kept as a stage child so it affects all clips uniformly.
     */
    private _fadeToBlackOverlay: Graphics | null = null;

    private _lastEffectiveBackground: string | null = null;

    // Renderer recovery (WebGL context loss / Pixi internal errors)
    private _rendererRecoveryPromise: Promise<void> | null = null;
    private _pendingRepaintTimeSec: number | null = null;
    private _lastRenderErrorAtMs = 0;

    // Async recompute queue for user-triggered re-renders (e.g. layout changes while paused).
    // Composition.computeFrameAtTime() does not await async clip updates, so rendering immediately after
    // mutating per-scene overrides can draw a stale frame. This queue coalesces multiple recompute requests
    // and renders only after all track updates have settled.
    private _asyncRecomputePromise: Promise<void> | null = null;
    private _pendingAsyncRecomputeTimeSec: number | null = null;

    public constructor({
                           height = 1080,
                           width = 1920,
                           background = '#000000',
                           backend = 'webgpu',
                           id = '',
                       }: Partial<CompositionSettings> = {}) {
        super();

        this.settings = {height, width, background, backend, id};
        this.isEmpty = true;
        this.compositionId = id;

        this.on('update', this.update.bind(this));
        this.on('attach', this.update.bind(this));
        this.on('detach', this.update.bind(this));
        this.on('load', this.update.bind(this));
        this.on('frame', this.update.bind(this));
        this.on('error', this.update.bind(this));

        const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
        this.audioCtx = new AudioCtx();
        this.masterGain = this.audioCtx.createGain();
        this.masterGain.connect(this.audioCtx.destination);

        AudioMixer.init(this.audioCtx, '/worklets/libretto-mixer.js', { output: this.masterGain }).then(mixer => {
            if (this._disposed) return;
            this.mixer = mixer;
        })

        autoDetectRenderer({...this.settings, preference: backend})
            .then(renderer => {
                if (this._disposed) {
                    try { (renderer as any)?.destroy?.(true); } catch {}
                    return;
                }
                this.renderer = renderer;
                this.attachRendererContextLossHandlers(renderer);
                this.trigger('init', undefined);
            })
            .catch(error => {
                console.error(error);
                this.trigger('error', new BaseError({
                    code: 'backendDetectionError',
                    message: `${error}`
                }));
            });
    }

    private attachRendererContextLossHandlers(renderer: Renderer): void {
        // Best-effort: handle WebGL context loss by recreating the renderer.
        // Screen-recording video + large textures can increase the likelihood of context loss.
        try {
            const canvas = (renderer as any)?.canvas as HTMLCanvasElement | undefined;
            if (!canvas || typeof canvas.addEventListener !== "function") return;

            canvas.addEventListener(
                "webglcontextlost",
                (event) => {
                    try { event.preventDefault?.(); } catch {}
                    console.warn("[Composition] WebGL context lost; recreating renderer");
                    if (this.playing) {
                        try { void this.pause(); } catch {}
                    }
                    void this.recreateRenderer();
                },
                // Must be non-passive to allow preventDefault.
                { passive: false } as any
            );

            canvas.addEventListener("webglcontextrestored", () => {
                console.warn("[Composition] WebGL context restored");
            });
        } catch {}
    }

    private pruneDestroyedDisplayObjects(container: Container): number {
        let removed = 0;
        const children = (container as any)?.children;
        if (!Array.isArray(children) || children.length === 0) return 0;

        for (let i = children.length - 1; i >= 0; i--) {
            const child: any = children[i];
            if (!child) {
                try { (container as any).removeChildAt?.(i); } catch {}
                removed += 1;
                continue;
            }

            // Sprite.destroy() sets `_texture = null`; rendering such an object can crash Pixi's batcher.
            const isDestroyed =
                Boolean(child?.destroyed) ||
                (child?.renderPipeId === "sprite" && child?._texture == null) ||
                (child?.renderPipeId === "graphics" && child?._geometry == null);

            if (isDestroyed) {
                try { (container as any).removeChild?.(child); } catch {}
                removed += 1;
                continue;
            }

            if (child?.children?.length) {
                removed += this.pruneDestroyedDisplayObjects(child as Container);
            }
        }

        return removed;
    }

    private async recreateRenderer(): Promise<void> {
        if (this._disposed) return;
        if (this._rendererRecoveryPromise) return this._rendererRecoveryPromise;

        const preference = this.settings.backend ?? "webgl";
        const settings = { ...this.settings };

        this._pendingRepaintTimeSec = this.currentTime?.seconds ?? 0;

        // Tear down existing renderer (best-effort).
        try { (this.renderer as any)?.destroy?.(true); } catch {}
        this.renderer = undefined;

        this._rendererRecoveryPromise = autoDetectRenderer({ ...settings, preference })
            .then((renderer) => {
                if (this._disposed) {
                    try { (renderer as any)?.destroy?.(true); } catch {}
                    return;
                }
                this.renderer = renderer;
                this.attachRendererContextLossHandlers(renderer);
                this.trigger("init", undefined);

                const tSec = this._pendingRepaintTimeSec;
                this._pendingRepaintTimeSec = null;
                if (tSec != null) {
                    try {
                        this.computeFrameAtTime(Timestamp.fromSeconds(tSec));
                    } catch {}
                }
            })
            .catch((error) => {
                console.error("[Composition] Failed to recreate renderer", error);
            })
            .finally(() => {
                this._rendererRecoveryPromise = null;
            });

        return this._rendererRecoveryPromise;
    }

    public getClipById(clipId: string): Clip | undefined {
        const id = String(clipId ?? "");
        if (!id) return undefined;
        for (const tr of this.tracks) {
            // @ts-ignore
            const found = tr.clips.find((c: any) => c?.id === id);
            if (found) return found;
        }
        return undefined;
    }

    private updateCurrentSceneContext(time: Timestamp): void {
        try {
            const scriptId = this.scriptLayer?.clipId;
            if (!scriptId) {
                this.currentSceneKey = "__start__";
                return;
            }
            const scriptClip: any = this.getClipById(scriptId);
            if (!scriptClip || typeof scriptClip.getScenes !== "function") {
                this.currentSceneKey = "__start__";
                return;
            }
            const scenes: any[] = Array.isArray(scriptClip.getScenes()) ? scriptClip.getScenes() : [];
            if (!scenes.length) {
                this.currentSceneKey = "__start__";
                return;
            }

            const t = Number(time?.seconds ?? 0);
            const scene =
                scenes.find((s: any) => t >= Number(s?.timelineStartSec ?? 0) && t < Number(s?.timelineEndSec ?? 0)) ??
                scenes.find((s: any) => t < Number(s?.timelineEndSec ?? 0)) ??
                scenes[scenes.length - 1];
            const key = scene?.startSplitId ? String(scene.startSplitId) : "__start__";
            this.currentSceneKey = key || "__start__";
        } catch {
            this.currentSceneKey = "__start__";
        }
    }

    public get height() {
        return this.settings.height;
    }

    public get width() {
        return this.settings.width;
    }

    public get background() {
        return this.settings.background;
    }

    public attachCanvasToComposition(canvas: HTMLCanvasElement): void {
        if (this._disposed) return;

        // Always re-attach if the canvas element changes (React can remount it).
        this.canvas = canvas;
        this.canvas.height = this.settings.height;
        this.canvas.width = this.settings.width;
        this.canvas.style.background = this.settings.background;
        this.context = this.canvas.getContext("2d")!;
        this.context.imageSmoothingEnabled = false;
        // Clear any stale pixels from a previous Composition that used this same DOM canvas.
        // (The Pixi renderer may not be ready yet, so we can't rely on computeFrame().)
        try {
            this.context.setTransform(1, 0, 0, 1, 0, 0);
            this.context.clearRect(0, 0, this.settings.width, this.settings.height);
        } catch {}

        // Render once the renderer is ready.
        const renderOnce = () => {
            if (this._disposed) return;
            try {
                this.computeFrame();
            } catch {}
        };
        if (this.renderer) {
            renderOnce();
            return;
        }

        if (this._renderOnInitListenerId) {
            try { this.off(this._renderOnInitListenerId); } catch {}
            this._renderOnInitListenerId = null;
        }
        const id = this.on("init", () => {
            try { this.off(id); } catch {}
            if (this._renderOnInitListenerId === id) this._renderOnInitListenerId = null;
            renderOnce();
        });
        this._renderOnInitListenerId = id;
    }

    /**
     * Recompute whether the composition is empty based on whether any track contains clips.
     * Emits `COMPOSITION_EMPTY` only when the boolean changes.
     */
    public recomputeEmptyState(): void {
        if (this._disposed) return;
        const hasAnyClips = this.tracks.some((t: any) => (t?.clips?.length ?? 0) > 0);
        if (hasAnyClips && this.isEmpty) {
            this.isEmpty = false;
            this.trigger(CompositionEventsEnum.COMPOSITION_EMPTY, { empty: false });
            return;
        }
        if (!hasAnyClips && !this.isEmpty) {
            this.isEmpty = true;
            this.trigger(CompositionEventsEnum.COMPOSITION_EMPTY, { empty: true });
        }
    }

    // Compute the currently active frame
    public computeFrame(): void {

        if (!this.renderer) {
            console.warn("No renderer attached to composition");
            return;
        }

        const t = Timestamp.fromFrames(this.frame, this.fps);
        this.computeFrameAtTime(t);

        if (this.playing) this.frame++;

    }

    public get playing(): boolean {
        return this.state === 'PLAY';
    }

    public get duration(): Timestamp {
        return this._duration;
    }

    public resize(width: number, height: number) {
        this.settings.width = width;
        this.settings.height = height;
        this.renderer?.resize(width, height);

        if (this.canvas) {
            this.canvas.height = height;
            this.canvas.width = width;
        }

        this.trigger('resize', undefined);
        this.trigger(CompositionEventsEnum.CANVAS_RESIZED, {width: width, height: height});

        // Resize the fade overlay (if present).
        if (this._fadeToBlackOverlay) {
            try {
                this._fadeToBlackOverlay.clear();
                this._fadeToBlackOverlay.beginFill(0x000000);
                this._fadeToBlackOverlay.drawRect(0, 0, width, height);
                this._fadeToBlackOverlay.endFill();
            } catch {}
        }
        this.computeFrame();
    }

    public changeBackground(background: hex) {
        this.settings.background = background;
        if (this.canvas) {
            try { this.canvas.style.background = background as string; } catch {}
        }
        try {
            // @ts-ignore
            if (this.renderer?.background) {
                // @ts-ignore
                this.renderer.background.color = background as unknown as number;
                // @ts-ignore
                this.renderer.background.alpha = 1;
            }
        } catch {}
        try {
            // @ts-ignore
            if (typeof this.renderer?.backgroundColor !== 'undefined') {
                // @ts-ignore
                this.renderer.backgroundColor = background as unknown as number;
            }
        } catch {}
        this.trigger(CompositionEventsEnum.CANVAS_BACKGROUND_CHANGED, { color: background });
        this.computeFrame();
    }

    /**
     * Ensure there is a dedicated track for the script layer and return it.
     * Creates a new base track if missing and stores its id on scriptLayer.
     */
    private ensureScriptLayerTrack(trackId?: string): Track<Clip> {
        // Initialize container if missing
        if (!this.scriptLayer) {
            this.scriptLayer = { layerId: null, clipId: null };
        }
        // If a desired track id is provided and exists, use it.
        if (trackId) {
            const existingById = this.tracks.find(t => t.id === trackId);
            if (existingById) {
                this.scriptLayer.layerId = trackId;
                try { (existingById as any).preserveWhenEmpty = true; } catch {}
                return existingById;
            }
        }
        // If there is a stored track id and it exists, return it
        if (this.scriptLayer.layerId) {
            const existing = this.tracks.find(t => t.id === this.scriptLayer!.layerId);
            if (existing) {
                try { (existing as any).preserveWhenEmpty = true; } catch {}
                return existing;
            }
        }
        // Otherwise, create a new track.
        const track = this.createTrack('base', trackId);
        this.scriptLayer.layerId = track.id;
        try { (track as any).preserveWhenEmpty = true; } catch {}
        return track;
    }

    /**
     * Prepare a clip for display without starting playback.
     * This ensures the clip can render when seeking (before play() is called).
     * - Ensures audio context and mixer are initialized
     * - Calls prepareForPlayback (registers mixer tracks, etc.)
     * - Resets video state so pullVideoFrame starts fresh
     * - Re-renders current frame
     */
    public async prepareClipForDisplay(clip: Clip): Promise<void> {
        // Ensure audio context exists
        if (!this.audioCtx) {
            const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
            this.audioCtx = new AC();
        }

        // Ensure mixer exists
        if (!this.mixer) {
            this.mixer = await AudioMixer.init(this.audioCtx, '/worklets/libretto-mixer.js', { output: this.masterGain });
        }

        // Prepare clip for playback (registers mixer tracks, etc.)
        if (typeof clip.prepareForPlayback === 'function') {
            await clip.prepareForPlayback(this.audioCtx, this.masterGain, this.mixer);
        }

        // Reset video state so pullVideoFrame will start fresh and return first frame
        // (warmVideoAt is for playback prep - it discards the first frame)
        const tDesired = this.currentTime.seconds;
        const onSeek = (clip as any).onSeek;
        if (typeof onSeek === 'function') {
            onSeek.call(clip, tDesired);
        }

        // ScriptLayerClip-specific pre-roll. This is separate from warmVideoAt so existing
        // warm behavior for other clips remains unchanged.
        const preRoll = (clip as any).preRollVideoAt;
        if (typeof preRoll === 'function') {
            try { await preRoll.call(clip, tDesired); } catch {}
        }

        // Re-render current frame - pullVideoFrame will now create a fresh iterator
        this.computeFrame();
    }

    /**
     * Insert an already-constructed clip into the script layer.
     * - Ensures a dedicated track exists
     * - Adds the clip to that track
     * - Updates scriptLayer.clipIds and emits SCRIPT_LAYER_UPDATED
     */
    public async insertClipIntoScriptLayer<L extends Clip>(clip: L, trackId?: string): Promise<L> {
        const track = this.ensureScriptLayerTrack(trackId);

        // Script layer track must contain exactly one ScriptClip.
        // Clear any existing clips on the script track before inserting the new one.
        // (Avoid adding first, because DefaultInsertStrategy will try to align/offset overlapping clips.)
        try {
            const existingClips: Clip[] = Array.isArray((track as any)?.clips) ? [...((track as any).clips as Clip[])] : [];
            for (const existing of existingClips) {
                try { (track as any).remove(existing); } catch {}
            }
        } catch {}

        await track.add(clip as never);
        // Always update scriptLayer with the new clip id
        this.scriptLayer = { layerId: track.id, clipId: clip.id };
        
        // Fire UI event
        this.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
            script: { layerId: this.scriptLayer.layerId, clipId: this.scriptLayer.clipId }
        });
        
        // Fire internal event with full state for scene bar initialization
        const scriptClip = clip as any;
        this.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
            clipId: clip.id,
            cuts: scriptClip.scriptCuts ?? [],
            gaps: scriptClip.gaps ?? [],
            segments: scriptClip.scriptSegments ?? [],
            splits: scriptClip.splits ?? [],
            duration: scriptClip.duration?.seconds ?? 0,
            hasVideo: (scriptClip.getVideoSources?.()?.length ?? 0) > 0,
        });
        
        return clip;
    }

    /**
     * Dispose this composition and free engine resources (audio, renderer, tickers).
     * Safe to call multiple times.
     */
    public async dispose(): Promise<void> {
        if (this._disposed) return;
        this._disposed = true;

        try {
            await this.pause();
        } catch {}

        try {
            this.stopRenderTicker();
        } catch {}

        // Detach tracks/clips without emitting editor events.
        try {
            for (const tr of [...this.tracks]) {
                try {
                    for (const clip of [...((tr as any).clips ?? [])]) {
                        try { clip.stopAudio?.(); } catch {}
                        try { clip.exit?.(); } catch {}
                        try { clip.off?.('*'); } catch {}
                    }
                } catch {}
                try { tr.off?.('*'); } catch {}
                try { if (tr.view?.parent) this.stage.removeChild(tr.view); } catch {}
            }
            this.tracks = [];
        } catch {}

        try { (this.stage as any)?.removeChildren?.(); } catch {}
        try { (this.stage as any)?.destroy?.({ children: true }); } catch {}

        try { (this.renderer as any)?.destroy?.(true); } catch {}
        this.renderer = undefined;

        // Detach canvas references to allow GC.
        this.canvas = undefined;
        this.context = undefined;

        try { await (this.audioCtx as any)?.close?.(); } catch {}
        try { (this.masterGain as any)?.disconnect?.(); } catch {}
        this.mixer = undefined;
    }

    public setPlaybackRate(r: number) {
        this.playbackRate = Math.max(0.1, r);
        // Nudge mixer transport (even in timer mode keeps schedules consistent)
        this.mixer?.setTransport({
            state: this.state === 'PLAY' ? 'play' : 'pause',
            compTime: this.compositionTimeNow,
            rate: this.playbackRate
        });
    }

    public async play(): Promise<void> {
        if (this._disposed) return;
        if (!this.audioCtx) {
            console.error("Audio context not initialized. Initializing now.");
            const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
            this.audioCtx = new AC();
        }

        if (!this.mixer) {
            console.error("Audio mixer not initialized. Initializing now.");
            this.mixer = await AudioMixer.init(this.audioCtx, '/worklets/libretto-mixer.js', { output: this.masterGain });
        }

        for (const tr of this.tracks) {
            for (const clip of tr.clips) {
                if (typeof clip.prepareForPlayback === 'function') {
                    await clip.prepareForPlayback(this.audioCtx, this.masterGain, this.mixer);
                }
            }
        }

        this.compileAndSendSchedule();

        // Anchor desired comp time BEFORE any awaits to avoid drift
        const tDesired = this.currentTime.seconds;

        for (const tr of this.tracks) for (const c of tr.clips) {
            const prime = (c as any).primeAudio;
            if (typeof prime === 'function') {
                await prime.call(c, tDesired, this.mixer!);
            }
            // Warm upcoming video so first frame is ready
            const warm = (c as any).warmVideoAt;
            if (typeof warm === 'function') {
                try { await warm.call(c, tDesired); } catch {}
            }
            // ScriptLayerClip-specific pre-roll path. Kept separate from warmVideoAt so
            // existing warm behavior is untouched.
            const preRoll = (c as any).preRollVideoAt;
            if (typeof preRoll === 'function') {
                try { await preRoll.call(c, tDesired); } catch {}
            }
        }

        await this.audioCtx.resume();

        // Choose master clock
        this.clockMode = this.hasAudibleAudioNowOrSoon(tDesired) ? 'audio' : 'timer';

        // Anchor the mapping compTime <-> chosen clock
        this.compStartAtPlay = tDesired;
        this.audioCtxStartAtPlay = this.audioCtx.currentTime;
        this.wallNowStartMs = performance.now();

        // Tell mixer to run (even if we’re in timer mode, audio schedule could be empty)
        this.mixer!.setTransport({state: 'play', compTime: tDesired, rate: this.playbackRate});


        this.state = 'PLAY';
        this.startRenderTicker();
        this.trigger('play', this.frame);
        this.trigger(CompositionEventsEnum.PLAYBACK_STATE, {isPlaying: true});
        this.trigger(CompositionEventsEnum.PLAYBACK_TIME, {time: tDesired});
    }

    private get compositionTimeNow(): number {
        if (this.clockMode === 'audio' && this.audioCtx) {
            const audioNow = this.audioCtx.currentTime;
            return this.compStartAtPlay + (audioNow - this.audioCtxStartAtPlay) * this.playbackRate;
        } else {
            const nowMs = performance.now();
            return this.compStartAtPlay + ((nowMs - this.wallNowStartMs) / 1000) * this.playbackRate;
        }
    }

    private stopRenderTicker() {
        if (this.rafId != null) cancelAnimationFrame(this.rafId);
        if (this.bgIntervalId != null) clearInterval(this.bgIntervalId);
        this.rafId = undefined;
        this.bgIntervalId = undefined;
    }

    private startRenderTicker() {
        this.stopRenderTicker();
        const tick = () => {
            if (this.state !== 'PLAY') return;

            const t = Math.min(this.compositionTimeNow, this.duration.seconds);

            // If we were timer-clocked and we’ve hit an audio span, switch to audio clock
            if (this.clockMode === 'timer' && this.hasAudibleAudioNowOrSoon(t)) {
                this.clockMode = 'audio';
                this.audioCtxStartAtPlay = this.audioCtx!.currentTime;
                this.compStartAtPlay = t;
            }

            this.trigger(CompositionEventsEnum.PLAYBACK_TIME, {time: t});

            // If you maintain an integer frame index for UI:
            const nextFrame = Math.floor(t * this.fps);
            if (nextFrame !== this.frame) this.frame = nextFrame;

            this.computeFrameAtTime(Timestamp.fromSeconds(t));

            if (t >= this.duration.seconds) {
                void this.pause();
                return;
            }
            this.rafId = requestAnimationFrame(tick);
        };
        this.rafId = requestAnimationFrame(tick);

        // Keep UI moving when tab is hidden
        this.bgIntervalId = window.setInterval(() => {
            if (this.state !== 'PLAY' || document.visibilityState === 'visible') return;
            const t = Math.min(this.compositionTimeNow, this.duration.seconds);

            this.trigger(CompositionEventsEnum.PLAYBACK_TIME, {time: t});

            const nextFrame = Math.floor(t * this.fps);
            this.frame = nextFrame;

            this.computeFrameAtTime(Timestamp.fromSeconds(t));
        }, 250) as unknown as number;
    }

    public async pause(): Promise<void> {
        if (!this.audioCtx) return;
        this.state = "IDLE";
        this.stopRenderTicker();
        this.trigger('pause', this.frame);
        this.trigger(CompositionEventsEnum.PLAYBACK_STATE, {isPlaying: false});
        const tNow = this.compositionTimeNow;
        this.trigger(CompositionEventsEnum.PLAYBACK_TIME, {time: tNow});

        // Freeze mixer transport at currentTime
        this.mixer?.setTransport({state: 'pause', compTime: tNow, rate: this.playbackRate});

        // Stop feeders (they’ll resume on next play/seek)
        for (const tr of this.tracks) {
            for (const clip of tr.clips) {
                clip.stopAudio?.();
            }
        }

        await this.audioCtx.suspend();
    }

    public get currentTime(): Timestamp {
        return Timestamp.fromFrames(this.frame, this.fps);
    }

    /**
     * Current playhead time in seconds.
     * - While playing, this is derived from the active master clock (audio or timer).
     * - While paused, this matches the current frame position.
     *
     * Used by clip feeders to apply backpressure (avoid decoding far ahead).
     */
    public getPlayheadTimeSec(): number {
        try {
            const t = this.playing ? this.compositionTimeNow : (this.frame / this.fps);
            return Math.max(0, Math.min(this.duration.seconds ?? Number.POSITIVE_INFINITY, t));
        } catch {
            return this.currentTime.seconds;
        }
    }

    public async seek(value: frame | Timestamp): Promise<void> {
        if (typeof value == 'number') {
            this.frame = Math.round(value > 0 ? value : 0);
        } else {
            this.frame = value.frames > 0 ? value.frames : 0;
        }

        // Compute target comp time and realign anchors
        const tTarget = this.frame / this.fps;
        this.compStartAtPlay = tTarget;
        this.audioCtxStartAtPlay = this.audioCtx?.currentTime ?? 0;
        this.wallNowStartMs = performance.now();

        // Quick fade-out to prevent click when seeking during playback
        const wasPlaying = this.playing;
        const FADE_TIME = 0.01; // 10ms fade
        if (wasPlaying && this.masterGain && this.audioCtx) {
            const now = this.audioCtx.currentTime;
            this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, now);
            this.masterGain.gain.linearRampToValueAtTime(0, now + FADE_TIME);
            // Wait for fade to complete
            await new Promise(resolve => setTimeout(resolve, FADE_TIME * 1000 + 5));
        }

        this.mixer?.setTransport({state: 'pause', compTime: tTarget, rate: this.playbackRate});
        for (const tr of this.tracks) for (const c of tr.clips) {
            const prime = (c as any).primeAudio;
            if (typeof (c as any).onSeek === 'function') {
                (c as any).onSeek(tTarget);
            }
            if (typeof prime === 'function') {
                await prime.call(c, tTarget, this.mixer!);
            }
            // ScriptLayerClip-specific pre-roll path. Existing warmVideoAt behavior
            // remains unchanged.
            const preRoll = (c as any).preRollVideoAt;
            if (typeof preRoll === 'function') {
                try {
                    await preRoll.call(c, tTarget);
                } catch {
                }
            }
        }
        // realign anchors, then:
        if (this.playing) {
            this.mixer?.setTransport({state: 'play', compTime: tTarget, rate: this.playbackRate});
        }

        // Fade back in after seek
        if (wasPlaying && this.masterGain && this.audioCtx) {
            const now = this.audioCtx.currentTime;
            this.masterGain.gain.setValueAtTime(0, now);
            this.masterGain.gain.linearRampToValueAtTime(1, now + FADE_TIME);
        }

        // In case we were timer-clocked and an audio region is about to start, switch clocks
        if (this.clockMode === 'timer' && this.hasAudibleAudioNowOrSoon(tTarget)) {
            this.clockMode = 'audio';
            this.audioCtxStartAtPlay = this.audioCtx?.currentTime ?? 0;
            this.compStartAtPlay = tTarget;
        }

        // Paint the frame at the new time
        this.computeFrameAtTime(Timestamp.fromSeconds(tTarget));
        this.trigger(CompositionEventsEnum.PLAYBACK_TIME, {time: tTarget});
    }

    public getTrackById(trackId: string): Track<Clip> | undefined {
        return this.tracks.find((t) => t.id === trackId);
    }

    public removeTrack<T extends Track<Clip>>(track: T): T | undefined {
        const index = this.tracks.findIndex((t) => t.id == track.id);

        if (track.view.parent) {
            this.stage.removeChild(track.view);
        }

        if (index != undefined && index >= 0) {
            this.tracks.splice(index, 1);
            this.trigger('detach', undefined);
            this.trigger(CompositionEventsEnum.LAYER_REMOVED, {layerId: track.id});
            this.recomputeEmptyState();
            track.off('*');
            return track;
        }
    }

    public shiftTrack<L extends Track<Clip>>(Track: (new () => L) | L): L {
        // Check if track has been instantiated
        const track = typeof Track == 'object' ? Track : new Track();

        track.connect(this);

        this.stage.addChild(track.view);
        this.tracks.unshift(track);

        track.bubble(this);
        this.trigger('update', undefined);
        this.trigger(CompositionEventsEnum.LAYER_ADDED, {layer: {id: track.id, clipIds: []}});

        return track;
    }

    public createTrack<T extends TrackType>(type: T, trackId?: string) {
        const track = TrackDeserializer.fromType({type, id: trackId});
        this.shiftTrack(track);

        return track;
    }

    public async add<L extends Clip>(clip: L, trackId?: string): Promise<Track<any>> {
        // If trackId is provided, check if track already exists
        let track: Track<any> | undefined;
        if (trackId) {
            track = this.getTrackById(trackId);
        }
        
        // If track doesn't exist, create a new one
        if (!track) {
            track = this.createTrack(clip.type, trackId);
        }
        
        try {
            await track.add(clip as never);
        } catch (e) {
            console.log("Error adding clip to track", e);
        }

        return track;
    }

    public async addAt<L extends Clip>(clip: L, index: number, trackId?: string): Promise<Track<any>> {
        // If trackId is provided, check if track already exists
        let track: Track<any> | undefined;
        if (trackId) {
            track = this.getTrackById(trackId);
        }
        
        // If track doesn't exist, create a new one
        if (!track) {
            track = this.createTrack(clip.type, trackId);
        }
        
        try {
            await track.add(clip as never, index);
        } catch (e) {
            console.log("Error adding clip to track", e);
        }

        return track;
    }

    private update(): void {
        this.recalculateDuration();
        this.computeFrame();
    }
    
    /**
     * Recalculate the composition duration based on all clips.
     * This is the source of truth for duration - call this after adding/removing/updating clips.
     */
    public recalculateDuration(): void {
        // Compute composition duration using segment-aware ends when present
        let maxEndSec = 0;
        for (const tr of this.tracks) {
            if (tr.disabled) continue;
            for (const clip of tr.clips) {
                const end = clip.start.seconds + clip.timelineDuration.seconds;
                if (end > maxEndSec) maxEndSec = end;
            }
        }
        // Set duration in seconds as source of truth; frames only for video stepping
        this._duration.seconds = Math.max(0, maxEndSec);
        this._duration.frames = Math.max(0, Math.round(maxEndSec * this.fps));
    }

    private hasAudibleAudioNowOrSoon(compTime: number, lookAheadSec = 0.25): boolean {
        for (const tr of this.tracks) for (const clip of tr.clips) {
            const handleId: string | undefined = (clip as any)._mixerTrack?.id;
            if (!handleId) continue;

            if (clip instanceof MediaClip) {
                const segs: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> = (clip as any).segments ?? [];
                if (Array.isArray(segs) && segs.length > 0) {
                    for (const seg of segs) {
                        const segDur = Math.max(0, (seg.srcEndSec ?? 0) - (seg.srcStartSec ?? 0));
                        const segRateRaw = Number((seg as any)?.playbackRate ?? (clip as any)?.playbackRate ?? 1.0);
                        const segRate = Number.isFinite(segRateRaw) && segRateRaw > 0 ? segRateRaw : 1.0;
	                        const start = seg.compStartSec ?? 0;
	                        const end = start + segDur / segRate;
	                        if (compTime + lookAheadSec >= start && compTime < end) {
	                            if (DEBUG_ENGINE_LOGS) {
	                                console.log("[AudibleSoon]", { clipId: (clip as any).id, compTime, start, end });
	                            }
	                            return true;
	                        }
	                    }
                    continue;
                }
            }

	            const start = (clip as any).start?.seconds ?? 0;
	            const end = (clip as any).stop?.seconds ?? Number.POSITIVE_INFINITY;
	            if (compTime + lookAheadSec >= start && compTime < end) {
	                if (DEBUG_ENGINE_LOGS) {
	                    console.log("[AudibleSoon]", { clipId: (clip as any).id, compTime, start, end });
	                }
	                return true;
	            }
	        }
        return false;
    }

    private compileAndSendSchedule() {
        if (!this.mixer) return;
        const events: any[] = [];
        for (const tr of this.tracks) for (const c of tr.clips) {
            // ScriptLayerClip: handle multiple audio sources, each with its own mixer track
            // Use scriptSegments to respect cuts
            if (c instanceof ScriptLayerClip) {
                const scheduleInfo = c.getAudioScheduleInfo();
                const clipStart = c.start?.seconds ?? 0;
                const segments = c.scriptSegments; // Derived segments (with cuts applied)
                
	                // If no segments (no cuts), fall back to full source scheduling
	                if (!segments || segments.length === 0) {
                        const sceneRateRaw = Number((c as any).playbackRate ?? 1.0);
                        const sceneRate = Number.isFinite(sceneRateRaw) && sceneRateRaw > 0 ? sceneRateRaw : 1.0;
	                    for (const { mixerTrack, entry, gain } of scheduleInfo) {
	                        const handleId = mixerTrack?.id;
	                        if (!handleId) continue;

	                        const entrySegments: any[] =
	                            Array.isArray((entry as any)?.segments) && (entry as any).segments.length > 0
	                                ? ((entry as any).segments as any[])
	                                : [];

	                        if (entrySegments.length > 0) {
	                            for (const seg of entrySegments) {
	                                const inSec = Number(seg?.range?.inSec ?? 0);
	                                const outSec = Number(seg?.range?.outSec ?? 0);
	                                const offsetSec = Number(seg?.offsetSec ?? 0);
	                                if (!Number.isFinite(inSec) || !Number.isFinite(outSec) || outSec <= inSec) continue;
	                                if (!Number.isFinite(offsetSec)) continue;
                                const sourceRate = Number(seg?.playbackRate ?? (entry as any).playbackRate ?? 1.0) || 1.0;
                                if (!Number.isFinite(sourceRate) || sourceRate <= 0) continue;
                                const rate = sourceRate * sceneRate;
	                                const sourceStart = clipStart + offsetSec / sceneRate;
	                                const sourceDur = (outSec - inSec) / rate;

		                                events.push({
		                                    clipHandleId: handleId,
		                                    busId: 'master',
		                                    start: sourceStart,
		                                    end: sourceStart + sourceDur,
		                                    // ScriptClip PCM is written in a virtual timeline domain (comp-time based).
		                                    srcOffset: sourceStart,
		                                    rate,
		                                    gain,
		                                    duck: null,
		                                });
	                            }
	                        } else {
	                            const inSec = entry.range?.inSec ?? 0;
	                            const outSec = entry.range?.outSec ?? Infinity;
	                            const offsetSec = entry.offsetSec ?? 0;
                            const sourceRate = Number(entry.playbackRate ?? 1.0) || 1.0;
                            if (!Number.isFinite(sourceRate) || sourceRate <= 0) continue;
	                            const rate = sourceRate * sceneRate;
	                            const sourceStart = clipStart + offsetSec / sceneRate;
	                            const sourceDur =
	                                outSec === Infinity
                                        ? Math.max(0, ((c.duration?.seconds ?? 0) - offsetSec) / sceneRate)
                                        : (outSec - inSec) / rate;

		                            events.push({
		                                clipHandleId: handleId,
		                                busId: 'master',
		                                start: sourceStart,
		                                end: sourceStart + sourceDur,
		                                // ScriptClip PCM is written in a virtual timeline domain (comp-time based).
		                                srcOffset: sourceStart,
		                                rate,
		                                gain,
		                                duck: null,
		                            });
	                        }
	                    }
	                } else {
	                    // Schedule each segment for each audio source
	                    // Segments are in clip-local time; need to map to each source's time
	                    for (const seg of segments) {
	                        const segDur = seg.srcEndSec - seg.srcStartSec;
	                        if (segDur <= 0) continue;
                            const segRateRaw = Number((seg as any)?.playbackRate ?? (c as any).playbackRate ?? 1.0);
                            const segRate = Number.isFinite(segRateRaw) && segRateRaw > 0 ? segRateRaw : 1.0;
	                        
	                        for (const { mixerTrack, entry, gain } of scheduleInfo) {
	                            const handleId = mixerTrack?.id;
	                            if (!handleId) continue;

	                            const entrySegments: any[] =
	                                Array.isArray((entry as any)?.segments) && (entry as any).segments.length > 0
	                                    ? ((entry as any).segments as any[])
	                                    : [
	                                          {
	                                              offsetSec: entry.offsetSec ?? 0,
	                                              range: entry.range ?? { inSec: 0, outSec: Infinity },
	                                              playbackRate: (entry as any).playbackRate ?? 1.0,
	                                          },
	                                      ];

	                            for (const sourceSeg of entrySegments) {
                                const sourceRate = Number(sourceSeg?.playbackRate ?? (entry as any).playbackRate ?? 1.0) || 1.0;
                                if (!Number.isFinite(sourceRate) || sourceRate <= 0) continue;
	                                const rate = sourceRate * segRate;

	                                const entryOffset = Number(sourceSeg?.offsetSec ?? 0);
	                                const entryRangeIn = Number(sourceSeg?.range?.inSec ?? 0);
	                                const entryRangeOut = sourceSeg?.range?.outSec ?? Infinity;
	                                const rangeOut = entryRangeOut === Infinity ? Infinity : Number(entryRangeOut);
	                                if (!Number.isFinite(entryOffset) || !Number.isFinite(entryRangeIn)) continue;
	                                if (rangeOut !== Infinity && (!Number.isFinite(rangeOut) || rangeOut <= entryRangeIn)) continue;

	                                const entryEndOnClip =
	                                    rangeOut === Infinity ? Infinity : entryOffset + (rangeOut - entryRangeIn) / sourceRate;

	                                // Segment is in clip-local time (0-based); check overlap with this segment placement.
	                                if (seg.srcEndSec <= entryOffset || seg.srcStartSec >= entryEndOnClip) {
	                                    continue;
	                                }

	                                const clampedStart = Math.max(seg.srcStartSec, entryOffset);
	                                const clampedEnd = Math.min(seg.srcEndSec, entryEndOnClip);
	                                const clampedDur = clampedEnd - clampedStart;
	                                if (clampedDur <= 0) continue;

		                                const compOffset = (clampedStart - seg.srcStartSec) / segRate;
		                                const timelineStart = seg.compStartSec + compOffset;

		                                events.push({
		                                    clipHandleId: handleId,
		                                    busId: 'master',
		                                    start: timelineStart,
		                                    end: timelineStart + clampedDur / segRate,
		                                    // ScriptClip PCM is written in a virtual timeline domain (comp-time based).
		                                    srcOffset: timelineStart,
		                                    rate,
		                                    gain,
		                                    duck: null,
		                                });
	                            }
	                        }
	                    }
	                }
                
                // Add crossfade overlay schedule events
                const crossfadeInfo = c.getCrossfadeScheduleInfo();
                if (crossfadeInfo) {
                    for (const xfade of crossfadeInfo) {
                        const handleId = xfade.mixerTrack?.id;
                        if (!handleId) continue;
                        
	                        events.push({
	                            clipHandleId: handleId,
	                            busId: 'master',
	                            start: xfade.start,
	                            end: xfade.end,
	                            srcOffset: xfade.srcOffset,
	                            rate: Number((xfade as any).rate ?? 1.0) || 1.0,
	                            gain: xfade.gain,
	                            duck: null,
	                        });
	                        
	                        if (DEBUG_ENGINE_LOGS) {
	                            console.log("[Mixer] scheduleEvent (crossfade)", {
	                                clipId: c.id,
	                                start: xfade.start,
	                                end: xfade.end,
	                            });
	                        }
	                    }
	                }
                
                continue;
            }

            const handleId = (c as any)._mixerTrack?.id;
            if (!handleId) continue;

            // MediaClip path: honor trims and timeline placement
            if (c instanceof MediaClip) {
                const trackGain = 1;
                const clipGain  = (c as any).gain ?? 1;
                const segs: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> = (c as any).segments ?? [];
                if (Array.isArray(segs) && segs.length > 0) {
                    for (const seg of segs) {
                        const segDur = Math.max(0, (seg.srcEndSec ?? 0) - (seg.srcStartSec ?? 0));
                        if (segDur <= 0) continue;
                        const start = seg.compStartSec ?? 0;
                        const inSec = seg.srcStartSec ?? 0;
	                        events.push({
	                            clipHandleId: handleId,
	                            busId: 'master',
	                            start,
	                            end: start + segDur,
	                            srcOffset: inSec,
	                            rate: (c as any).playbackRate ?? 1,
	                            gain: trackGain * clipGain,
	                            duck: null,
	                        });
	                        if (DEBUG_ENGINE_LOGS) {
	                            console.log("[Mixer] scheduleEvent (media-seg)", {
	                                clipId: (c as any).id,
	                                start,
	                                end: start + segDur,
	                                srcOffset: inSec,
	                                dur: segDur,
	                            });
	                        }
	                    }
	                    continue;
                } else {
                    // Fallback to single-span behavior
                    const start = c.start.seconds ?? 0; // timeline start
                    const inSec  = (c as any).range?.[0]?.seconds ?? 0;
                    const outSec = (c as any).range?.[1]?.seconds ?? inSec;
                    const dur    = Math.max(0, outSec - inSec);
	                    events.push({
	                        clipHandleId: handleId,
	                        busId: 'master',
	                        start,
	                        end: start + dur,
	                        srcOffset: inSec,
	                        rate: (c as any).playbackRate ?? 1,
	                        gain: trackGain * clipGain,
	                        duck: null,
	                    });
	                    if (DEBUG_ENGINE_LOGS) {
	                        console.log("[Mixer] scheduleEvent (media-fallback)", {
	                            clipId: (c as any).id,
	                            start,
	                            end: start + dur,
	                            srcOffset: inSec,
	                            dur,
	                        });
	                    }
	                    continue;
	                }
	            }

            // Base clip fallback: schedule full duration
            const s = (c as any).start?.seconds ?? 0;
            const e = (c as any).stop?.seconds ?? s;
            const d = Math.max(0, e - s);
	            events.push({
	                clipHandleId: handleId,
	                busId: 'master',
	                start: s,
	                end: s + d,
	                srcOffset: 0,
	                rate: (c as any).playbackRate ?? 1,
	                gain: 1,
	                duck: null,
	            });
	            if (DEBUG_ENGINE_LOGS) {
	                console.log("[Mixer] scheduleEvent (base)", { clipId: (c as any).id, start: s, end: s + d, srcOffset: 0, dur: d });
	            }
	        }

        this.mixer.setSchedule({ events });
    }

    /**
     * Call this when a clip's timing (offset/range) changes so the audio schedule and feeders
     * reflect the new placement. Keeps transport continuous when playing.
     */
	    public async onClipTimingChanged(): Promise<void> {
	        // 1) Recompute schedule for the mixer (reflects new offset/range placement)
	        const t = this.compositionTimeNow;
	        if (DEBUG_ENGINE_LOGS) {
	            console.log("[Composition.onClipTimingChanged]", { playing: this.playing, compTime: t });
	        }

        this.compileAndSendSchedule();
        for (const tr of this.tracks) for (const c of tr.clips) {
            if (typeof (c as any).onSeek === 'function') {
                try {
                    (c as any).onSeek(t);
                } catch {
                }
            }
            if (typeof (c as any).warmVideoAt === 'function') {
                try {
                    await (c as any).warmVideoAt(t);
                } catch {
                }
            }
            const preRoll = (c as any).preRollVideoAt;
            if (typeof preRoll === 'function') {
                try {
                    await preRoll.call(c, t);
                } catch {
                }
            }
        }

        if (this.playing) {
            // 2a) While playing: flush + prebuffer (prime) at current time
            for (const tr of this.tracks) for (const c of tr.clips) {
                const prime = (c as any).primeAudio;
                if (typeof prime === 'function') {
                    await prime.call(c, t, this.mixer!);
                }
            }
            // Keep transport continuous at the same comp time
            this.mixer?.setTransport({ state: 'play', compTime: t, rate: this.playbackRate });
        } else {
            // 2b) While paused: stop any active feedners; play() will prime before resuming
            for (const tr of this.tracks) for (const c of tr.clips) {
                if (c instanceof AudioClip || c instanceof VideoClip) {
                    c.stopAudio?.();
                    if (c.mixerTrack && this.mixer) {
                        const clipLocal = c.CompositionTimeToClipTime(t);
                        this.mixer.flushTrack(c.mixerTrack as unknown as MixerTrackHandle, clipLocal);
                    }
                }
            }
        }
    }

    private applySceneContextAndTransitions(ts: Timestamp): string {
        // Always update scene key first so clips can use it during update().
        this.updateCurrentSceneContext(ts);

        // Scene transitions + per-scene canvas background.
        // - Fade: composition-level fade-to-black overlay, and background swaps at black.
        // - Smart: interpolate background color between from/to scenes with easing.
        let fadeOverlayAlpha = 0;
        let effectiveBackground = normalizeHexColor(this.settings.background) ?? "#000000";
        let transitionRuntime: SceneTransitionRuntimeInfo = null;

        try {
            const scriptId = this.scriptLayer?.clipId;
            const clip = scriptId ? (this.getClipById(scriptId) as any) : null;
            if (clip && clip instanceof ScriptLayerClip) {
                const clipLocal = clip.mapCompTimeToClipLocal?.(ts.seconds);
                if (clipLocal != null) {
                    const info = clip.getTransitionAtTime?.(clipLocal) as any;
                    const scenes: any[] = typeof clip.getScenes === "function" ? clip.getScenes() : [];

                    const sceneKeyForIndex = (idx: number): string => {
                        const s = scenes.find((sc: any) => Number(sc?.index) === Number(idx)) ?? scenes[idx];
                        const key = s?.startSplitId ? String(s.startSplitId) : "__start__";
                        return key || "__start__";
                    };

                    const sceneBgForIndex = (idx: number): string => {
                        try {
                            const props = clip.getSceneProperties?.(idx) as any;
                            const c = normalizeHexColor(props?.backgroundColor);
                            return c ?? effectiveBackground;
                        } catch {
                            return effectiveBackground;
                        }
                    };

                    if (info) {
                        const p = clamp01(Number(info.progress ?? 0));
                        const fromBg = sceneBgForIndex(Number(info.fromSceneIndex));
                        const toBg = sceneBgForIndex(Number(info.toSceneIndex));

                        transitionRuntime = {
                            fromSceneIndex: Number(info.fromSceneIndex),
                            toSceneIndex: Number(info.toSceneIndex),
                            fromSceneKey: sceneKeyForIndex(Number(info.fromSceneIndex)),
                            toSceneKey: sceneKeyForIndex(Number(info.toSceneIndex)),
                            progress: p,
                            transitionType: String(info.transitionType ?? "cut"),
                            easing: typeof info.easing === "string" ? String(info.easing) : undefined,
                            addFades: Boolean(info.addFades ?? false),
                        };

                        if (transitionRuntime.transitionType === "fade") {
                            fadeOverlayAlpha = Math.sin(p * Math.PI);
                            // Swap background at black (midpoint).
                            effectiveBackground = p >= 0.5 ? toBg : fromBg;
                        } else {
                            const eased = applyEasing(p, transitionRuntime.easing);
                            effectiveBackground = lerpColorHex(fromBg, toBg, eased);
                        }
                    } else {
                        // No transition: use the active scene's background override if present.
                        const sceneIndex = clip.findSceneIndexForTime?.(clipLocal) ?? -1;
                        if (Number.isFinite(sceneIndex) && sceneIndex >= 0) {
                            effectiveBackground = sceneBgForIndex(sceneIndex);
                        }
                    }
                }
            }
        } catch {}

        this.currentSceneTransition = transitionRuntime;

        if (!this._fadeToBlackOverlay) {
            const g = new Graphics();
            try { (g as any).eventMode = "none"; } catch {}
            g.beginFill(0x000000);
            g.drawRect(0, 0, this.settings.width, this.settings.height);
            g.endFill();
            g.alpha = 0;
            this.stage.addChild(g);
            this._fadeToBlackOverlay = g;
        }

        if (this._fadeToBlackOverlay) {
            // Keep overlay at the top of the stage so it always covers everything.
            try { this.stage.addChild(this._fadeToBlackOverlay); } catch {}
            this._fadeToBlackOverlay.alpha = fadeOverlayAlpha;
        }

        if (this.canvas && this._lastEffectiveBackground !== effectiveBackground) {
            this._lastEffectiveBackground = effectiveBackground;
            try { this.canvas.style.background = effectiveBackground as any; } catch {}
        }

        return effectiveBackground;
    }

    /**
     * Prepare internal scene context and transition state for a given timestamp,
     * returning the effective canvas background color for that frame.
     *
     * Export uses this to match live preview while still awaiting async clip updates.
     */
    public prepareFrameForRender(ts: Timestamp): string {
        return this.applySceneContextAndTransitions(ts);
    }

    private computeFrameAtTime(ts: Timestamp): void {
        const renderer = this.renderer as any;
        if (!renderer) return;
        if (renderer?.destroyed) {
            void this.recreateRenderer();
            return;
        }

        const effectiveBackground = this.applySceneContextAndTransitions(ts);

        for (let i = 0; i < this.tracks.length; i++) this.tracks[i].update(ts);

        // Always clear the renderer target before rendering so stale pixels never persist
        // when clips are removed or when a transparent scene doesn't cover the full canvas.
        try {
            renderer.render({
                container: this.stage,
                clear: true,
                clearColor: toPixiClearColor(effectiveBackground) as any,
            });
        } catch (error) {
            // Pixi can throw here if the WebGL context is lost or if a destroyed display object
            // is still present in the scene graph (commonly manifests as Batcher.ts null access).
            const now = Date.now();
            if (now - this._lastRenderErrorAtMs > 1000) {
                this._lastRenderErrorAtMs = now;
                console.warn("[Composition] Renderer error; attempting recovery", error);
            }

            // First try pruning destroyed objects and re-render once (cheap + often enough).
            try {
                const removed = this.pruneDestroyedDisplayObjects(this.stage);
                if (removed > 0) {
                    renderer.render({
                        container: this.stage,
                        clear: true,
                        clearColor: toPixiClearColor(effectiveBackground) as any,
                    });
                }
            } catch {}

            if (this.playing) {
                try { void this.pause(); } catch {}
            }
            this._pendingRepaintTimeSec = ts.seconds;
            void this.recreateRenderer();
            return;
        }

        try {
            this.context?.clearRect(0, 0, this.settings.width, this.settings.height);
            this.context?.drawImage(renderer.canvas, 0, 0);
        } catch {}
        this.trigger('currentframe', this.frame);
    }

    public recomputeFrameAtCurrentTime(): void {
        this.computeFrameAtTime(this.currentTime);
    }

    private async computeFrameAtTimeAwaitingUpdates(ts: Timestamp): Promise<void> {
        const renderer = this.renderer as any;
        if (!renderer) return;
        if (renderer?.destroyed) {
            await this.recreateRenderer();
            return;
        }

        const effectiveBackground = this.applySceneContextAndTransitions(ts);

        const updatePromises: Promise<void>[] = [];
        for (let i = 0; i < this.tracks.length; i++) {
            try {
                const maybe = this.tracks[i].update(ts);
                if (maybe && typeof (maybe as any).then === "function") {
                    updatePromises.push(maybe as Promise<void>);
                }
            } catch {}
        }

        if (updatePromises.length) {
            await Promise.allSettled(updatePromises);
        }

        try {
            renderer.render({
                container: this.stage,
                clear: true,
                clearColor: toPixiClearColor(effectiveBackground) as any,
            });
        } catch (error) {
            const now = Date.now();
            if (now - this._lastRenderErrorAtMs > 1000) {
                this._lastRenderErrorAtMs = now;
                console.warn("[Composition] Renderer error (awaiting updates); attempting recovery", error);
            }
            try {
                const removed = this.pruneDestroyedDisplayObjects(this.stage);
                if (removed > 0) {
                    renderer.render({
                        container: this.stage,
                        clear: true,
                        clearColor: toPixiClearColor(effectiveBackground) as any,
                    });
                }
            } catch {}
            this._pendingRepaintTimeSec = ts.seconds;
            await this.recreateRenderer();
            return;
        }

        try {
            this.context?.clearRect(0, 0, this.settings.width, this.settings.height);
            this.context?.drawImage(renderer.canvas, 0, 0);
        } catch {}
        this.trigger("currentframe", this.frame);
    }

    /**
     * Recompute the current frame and await async clip updates before rendering.
     * This is intended for user-triggered redraws while paused (e.g. applying a scene layout),
     * so the canvas reflects updated per-scene overrides immediately without requiring playback.
     */
    public async recomputeFrameAtCurrentTimeAsync(): Promise<void> {
        if (this._disposed) return;
        if (!this.renderer) return;

        const tSec = Number(this.currentTime?.seconds ?? 0);
        this._pendingAsyncRecomputeTimeSec = tSec;

        if (this._asyncRecomputePromise) {
            return this._asyncRecomputePromise;
        }

        this._asyncRecomputePromise = (async () => {
            while (this._pendingAsyncRecomputeTimeSec != null) {
                const nextSec = this._pendingAsyncRecomputeTimeSec;
                this._pendingAsyncRecomputeTimeSec = null;
                try {
                    await this.computeFrameAtTimeAwaitingUpdates(Timestamp.fromSeconds(nextSec));
                } catch {
                    // Keep the queue alive for subsequent recompute requests.
                }
            }
        })();

        try {
            await this._asyncRecomputePromise;
        } finally {
            this._asyncRecomputePromise = null;
        }
    }

    /**
     * Capture a screenshot of the composition at a specific time.
     * This renders the frame at the given time and returns a data URL.
     * Does not affect playback state.
     */
    public async screenshotAt(
        timeSec: number,
        format: 'png' | 'jpeg' | 'webp' = 'jpeg',
        quality = 0.8,
        opts?: { maxWidthPx?: number; restore?: boolean }
    ): Promise<string | null> {
        if (!this.renderer) return null;

        const restore = opts?.restore !== false;
        const maxWidthPx = Number(opts?.maxWidthPx ?? 0);

        // Store current frame/time so we can restore visible state after capturing (live composition use-case).
        const originalFrame = this.frame;
        const originalTimeSec = originalFrame / this.fps;

        try {
            // Render at target time
            const targetFrame = Math.round(timeSec * this.fps);
            this.frame = targetFrame;

            // Reset video state for all clips so they render the correct frame
            for (const tr of this.tracks) {
                for (const c of tr.clips) {
                    if (typeof (c as any).onSeek === 'function') {
                        (c as any).onSeek(timeSec);
                    }
                }
            }

            const ts = Timestamp.fromSeconds(timeSec);
            // Match export: prepare scene context + transitions, then await async clip updates.
            let effectiveBackground = this.settings.background as any;
            try {
                effectiveBackground = this.prepareFrameForRender(ts);
            } catch {
                try {
                    this.updateCurrentSceneContext(ts);
                } catch {}
            }

            const updatePromises: Promise<void>[] = [];
            for (let i = 0; i < this.tracks.length; i++) {
                try {
                    const maybe = this.tracks[i].update(ts);
                    if (maybe && typeof (maybe as any).then === 'function') {
                        updatePromises.push(maybe as Promise<void>);
                    }
                } catch {}
            }
            if (updatePromises.length) {
                await Promise.allSettled(updatePromises);
            }

            this.renderer.render({
                container: this.stage,
                clear: true,
                clearColor: toPixiClearColor(effectiveBackground) as any,
            });

            // Capture from the renderer canvas so the on-screen canvas doesn't flicker.
            const srcCanvas = this.renderer.canvas as HTMLCanvasElement;
            if (Number.isFinite(maxWidthPx) && maxWidthPx > 0 && srcCanvas.width > maxWidthPx) {
                try {
                    const targetW = Math.max(1, Math.round(maxWidthPx));
                    const targetH = Math.max(1, Math.round((targetW * srcCanvas.height) / srcCanvas.width));
                    const outCanvas = document.createElement("canvas");
                    outCanvas.width = targetW;
                    outCanvas.height = targetH;
                    const ctx = outCanvas.getContext("2d", { willReadFrequently: false });
                    if (ctx) {
                        ctx.imageSmoothingEnabled = true;
                        try {
                            (ctx as any).imageSmoothingQuality = "high";
                        } catch {}
                        ctx.drawImage(srcCanvas as any, 0, 0, targetW, targetH);
                        return outCanvas.toDataURL(`image/${format}`, quality);
                    }
                } catch {}
            }

            return srcCanvas.toDataURL(`image/${format}`, quality);
        } catch (e) {
            console.warn("[Composition.screenshotAt] Failed:", e);
            return null;
        } finally {
            if (restore) {
                // Restore original frame and re-render the visible canvas at the original time.
                this.frame = originalFrame;
                for (const tr of this.tracks) {
                    for (const c of tr.clips) {
                        if (typeof (c as any).onSeek === 'function') {
                            (c as any).onSeek(originalTimeSec);
                        }
                    }
                }
                try {
                    this.computeFrameAtTime(Timestamp.fromSeconds(originalTimeSec));
                } catch {}
            }
        }
    }
}
